import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Box,
  Progress,
  Table,
  TableContainer,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

function FrameworksTable({ data, columns, isLoading, onRowClick }) {
  const { t } = useTranslation("translation", { keyPrefix: "dataTable" });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  const rows = data && data.length > 0 ? table.getRowModel().rows : [];

  return (
    <Box
      pos="relative"
      borderWidth="thin"
      borderRadius="md"
      overflow="hidden"
      backgroundColor="white"
    >
      {isLoading && (
        <Progress
          size="xs"
          isIndeterminate
          pos="absolute"
          insetInline="0"
          insetBlockStart="0"
          zIndex="3"
          colorScheme="customScheme"
        />
      )}
      <TableContainer overflowY="hidden">
        <Table size="md">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    style={{
                      verticalAlign: "top",
                      paddingInline: "var(--chakra-space-4)",
                      ...(header.getSize() ? { minWidth: header.getSize(), maxWidth: header.getSize() } : {}),
                    }}
                    pos="sticky"
                    insetBlockStart="0"
                    zIndex={1}
                    borderBlockEndWidth="thin"
                    userSelect="none"
                    cursor="default"
                    fontSize="xs"
                    fontWeight="bold"
                    color="gray.600"
                    textTransform="uppercase"
                    letterSpacing="normal"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
        </Table>
      </TableContainer>
      <Box
        overflowY="auto"
        maxHeight="500px"
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
      >
        <Table size="md">
          <Tbody>
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={999} textAlign="center">
                  {t("noDataMessage")}
                </Td>
              </Tr>
            )}
            {rows.map((row) => (
              <Tr
                key={row.id}
                cursor={onRowClick ? "pointer" : undefined}
                _hover={onRowClick ? { bg: "gray.50" } : undefined}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    whiteSpace="pre-wrap"
                    style={{
                      paddingInline: "var(--chakra-space-4)",
                      ...(cell.column.getSize() ? { minWidth: cell.column.getSize(), maxWidth: cell.column.getSize() } : {}),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default FrameworksTable;
